/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  HorizontalStoryFeedData,
  newsFeedApi,
  NewsFeedArgs,
  NewsFeedFilter,
  NewsFeedInAppMessageData,
  NewsFeedItem,
  NewsFeedType,
  StoryCard,
  storyCardApi,
  StoryCardItemData,
  StoryCardNewsfeedPinnedStatusUpdateArgs,
} from '../services';
import { isAdminLayout } from '../shared';
import {
  addCardToBookmarks,
  addComment,
  deleteCard,
  deleteComment,
  editComment,
  eventDelete,
  eventUpdateChannels,
  getStoryFeedHandler,
  hideComment,
  highlightComment,
  pollVote,
  reactOnCard,
  reactOnComment,
  removeCardFromBookmarks,
  repostCard,
  toggleCardSection,
  togglePublishOnCard,
  updateCardEvent,
} from './common';
import {
  addCommentReducer,
  deleteCommentReducer,
  editCommentReducer,
  hideCommentReducer,
  highlightCommentReducer,
  pollVoteReducer,
  reactOnCardReducer,
  reactOnCommentReducer,
} from './extraReducers';

interface NewsFeedHandlerArgs extends NewsFeedArgs {
  isPersonalizationEnabled?: boolean;
}

const newsFeedHandler = async (
  { isPersonalizationEnabled, ...args }: NewsFeedHandlerArgs,
  { dispatch }: any
) => {
  if (!isPersonalizationEnabled || isAdminLayout()) {
    return dispatch(newsFeedApi.endpoints.newsFeed.initiate(args)).unwrap();
  }

  const { channelId, page, size } = args;

  return dispatch(
    newsFeedApi.endpoints.newsFeedPersonalized.initiate({ channelId, page, size })
  ).unwrap();
};

export const getNewsFeed = createAsyncThunk('newsFeed', newsFeedHandler);

export const loadMoreNewsFeed = createAsyncThunk('newsFeed/loadMore', newsFeedHandler);

export const getPinnedFeed = createAsyncThunk('pinnedFeed', getStoryFeedHandler);

export const loadMorePinnedFeed = createAsyncThunk('pinnedFeed/loadMore', getStoryFeedHandler);

export const horizontalStoryFeedLoadMore = createAsyncThunk(
  'horizontalStoryFeed/loadMore',
  getStoryFeedHandler
);

export const getSubTabFeed = createAsyncThunk('subTabFeed', getStoryFeedHandler);

export const loadMoreSubTabFeed = createAsyncThunk('subTabFeed/loadMore', getStoryFeedHandler);

export const storyCardNewsfeedPinnedStatusUpdate = createAsyncThunk(
  'storyCardNewsfeedPinnedStatusUpdate',
  async (args: StoryCardNewsfeedPinnedStatusUpdateArgs, { dispatch }) => {
    return dispatch(
      storyCardApi.endpoints.storyCardNewsfeedPinnedStatusUpdate.initiate(args)
    ).unwrap();
  }
);

const getNewsFeedCard = (newsFeed: NewsFeedItem[], id: number) =>
  (
    newsFeed.find(
      ({ type, data }) =>
        type === NewsFeedType.STORY_CARD_ITEM && (data as StoryCardItemData).item.id === id
    )?.data as StoryCardItemData
  )?.item;

const getNewsFeedHorizontalCard = (newsFeed: NewsFeedItem[], id: number) =>
  (
    newsFeed.find(
      ({ type, data }) =>
        type === NewsFeedType.HORIZONTAL_STORY_FEED &&
        (data as HorizontalStoryFeedData).items.find((item) => item.id === id)
    )?.data as HorizontalStoryFeedData
  )?.items.find((item) => item.id === id);

const getNewsFeedCardIndex = (newsFeed: NewsFeedItem[], id: number) =>
  newsFeed.findIndex(
    ({ type, data }) =>
      type === NewsFeedType.STORY_CARD_ITEM && (data as StoryCardItemData).item.id === id
  );

const getNewsFeedHorizontalIndex = (newsFeed: NewsFeedItem[], id: number) =>
  newsFeed.findIndex(
    ({ type, data }) =>
      type === NewsFeedType.HORIZONTAL_STORY_FEED &&
      (data as HorizontalStoryFeedData).items.find((item) => item.id === id)
  );

interface NewsFeedState {
  isFetched: boolean;
  newsFeed: NewsFeedItem[];
  page: number;
  hasNextPage: boolean;
  filter?: NewsFeedFilter;
}

const initialState: NewsFeedState = {
  isFetched: false,
  newsFeed: [],
  page: 0,
  hasNextPage: false,
  filter: undefined,
};

const newsFeedSlice = createSlice({
  name: 'newsFeed',
  initialState,
  reducers: {
    inAppMessageRemove: (state, action: PayloadAction<number>) => {
      state.newsFeed = state.newsFeed.filter(
        ({ type, data }) =>
          type !== NewsFeedType.IN_APP_MESSAGE ||
          (data as NewsFeedInAppMessageData).id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNewsFeed.pending, (state) => {
        state.isFetched = false;
      })
      .addCase(getNewsFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.isFetched = true;
        state.newsFeed = items;
        state.page = page;
        state.hasNextPage = hasNextPage;
        state.filter = action.meta.arg.filter;
      })
      .addCase(loadMoreNewsFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.newsFeed.push(...items);
        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(horizontalStoryFeedLoadMore.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;

        const horizontalStoryFeed = state.newsFeed.find(
          ({ type }) => type === NewsFeedType.HORIZONTAL_STORY_FEED
        );

        if (horizontalStoryFeed) {
          const horizontalStoryFeedData = horizontalStoryFeed.data as HorizontalStoryFeedData;
          horizontalStoryFeedData.items.push(...items);
          horizontalStoryFeedData.pageInfo = pageInfo;
        }
      })
      .addCase(getPinnedFeed.pending, (state) => {
        state.isFetched = false;
      })
      .addCase(getPinnedFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.isFetched = true;

        state.newsFeed = items.map((item: StoryCard) => {
          return {
            type: NewsFeedType.STORY_CARD_ITEM,
            data: { item },
          };
        });

        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(loadMorePinnedFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.newsFeed = [
          ...state.newsFeed,
          ...items.map((item: StoryCard) => {
            return {
              type: NewsFeedType.STORY_CARD_ITEM,
              data: { item },
            };
          }),
        ];

        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(getSubTabFeed.pending, (state) => {
        state.isFetched = false;
      })
      .addCase(getSubTabFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.isFetched = true;

        state.newsFeed = items.map((item: StoryCard) => {
          return {
            type: NewsFeedType.STORY_CARD_ITEM,
            data: { item },
          };
        });

        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(loadMoreSubTabFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.newsFeed = [
          ...state.newsFeed,
          ...items.map((item: StoryCard) => {
            return {
              type: NewsFeedType.STORY_CARD_ITEM,
              data: { item },
            };
          }),
        ];

        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const { itemId, comment } = action.payload;

        const card = getNewsFeedCard(state.newsFeed, itemId);

        if (!card) {
          return;
        }

        addCommentReducer({ card, comment });
      })
      .addCase(editComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = getNewsFeedCard(state.newsFeed, storyCardId);

        if (!card) {
          return;
        }

        editCommentReducer({ card, comment });
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = getNewsFeedCard(state.newsFeed, storyCardId);

        if (!card) {
          return;
        }

        hideCommentReducer({ card, comment });
      })
      .addCase(highlightComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = getNewsFeedCard(state.newsFeed, storyCardId);

        if (!card) {
          return;
        }

        highlightCommentReducer({ card, comment });
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { deleted, cardId, commentId, parentId, repliesCount } = action.payload;

        if (!deleted) {
          return;
        }

        const card = getNewsFeedCard(state.newsFeed, cardId);

        if (!card) {
          return;
        }

        deleteCommentReducer({ card, commentId, parentId, repliesCount });
      })
      .addCase(reactOnComment.fulfilled, (state, action) => {
        const { reactions, cardId, commentId } = action.payload;

        const card = getNewsFeedCard(state.newsFeed, cardId);

        if (!card) {
          return;
        }

        reactOnCommentReducer({ card, commentId, reactions });
      })
      .addCase(reactOnCard.fulfilled, (state, action) => {
        const { cardId, reaction, reactions } = action.payload;

        const card = getNewsFeedCard(state.newsFeed, cardId);

        if (!card) {
          return;
        }

        reactOnCardReducer({ card, reaction, reactions });
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        const { deleted, storyCardId } = action.payload;

        if (!deleted) {
          return;
        }

        const cardIndex = getNewsFeedCardIndex(state.newsFeed, storyCardId);

        if (cardIndex !== -1) {
          state.newsFeed.splice(cardIndex, 1);
        }

        const horizontalIndex = getNewsFeedHorizontalIndex(state.newsFeed, storyCardId);

        if (horizontalIndex === -1) {
          return;
        }

        const horizontal = state.newsFeed[horizontalIndex];
        const horizontalData = horizontal.data as HorizontalStoryFeedData;
        const horizontalFilteredItems = horizontalData.items.filter(({ id }) => id !== storyCardId);

        state.newsFeed[horizontalIndex] = {
          ...horizontal,
          data: { ...horizontalData, items: horizontalFilteredItems },
        };
      })
      .addCase(togglePublishOnCard.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!payload) {
          return;
        }

        const { id: storyCardId, status } = payload;

        const cardIndex = getNewsFeedCardIndex(state.newsFeed, storyCardId);

        if (cardIndex === -1) {
          return;
        }

        if (state.filter?.status?.length && !state.filter.status.includes(status)) {
          state.newsFeed.splice(cardIndex, 1);
          return;
        }

        (state.newsFeed[cardIndex].data as StoryCardItemData).item.status = status;
      })
      .addCase(repostCard.fulfilled, (state, action) => {
        const storyCard = action.payload;

        if (!storyCard) {
          return;
        }

        const card = getNewsFeedCard(state.newsFeed, storyCard.id);

        if (!card) {
          return;
        }

        card.dateToDisplay = storyCard.dateToDisplay;
      })
      .addCase(toggleCardSection.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!payload) {
          return;
        }

        const { id: storyCardId, section } = payload;

        const card = getNewsFeedCard(state.newsFeed, storyCardId);

        if (!card) {
          return;
        }

        card.section = section;
      })
      .addCase(addCardToBookmarks.fulfilled, (state, action) => {
        const { payload, storyCardId } = action.payload;

        if (!payload) {
          return;
        }

        const horizontalCard = getNewsFeedHorizontalCard(state.newsFeed, storyCardId);

        if (horizontalCard) {
          horizontalCard.isBookmarked = true;
        }

        const card = getNewsFeedCard(state.newsFeed, storyCardId);

        if (card) {
          card.isBookmarked = true;
        }
      })
      .addCase(removeCardFromBookmarks.fulfilled, (state, action) => {
        const { payload, storyCardId } = action.payload;

        if (!payload) {
          return;
        }

        const horizontalCard = getNewsFeedHorizontalCard(state.newsFeed, storyCardId);

        if (horizontalCard) {
          horizontalCard.isBookmarked = false;
        }

        const card = getNewsFeedCard(state.newsFeed, storyCardId);

        if (card) {
          card.isBookmarked = false;
        }
      })
      .addCase(pollVote.fulfilled, (state, action) => {
        const { payload, storyCardId, selectedVoteIds } = action.payload;

        if (!payload) {
          return;
        }

        const card = getNewsFeedCard(state.newsFeed, storyCardId);

        if (!card) {
          return;
        }

        pollVoteReducer({ card, selectedVoteIds });
      })
      .addCase(eventDelete.fulfilled, (state, action) => {
        const { deleted, eventId } = action.payload;

        if (!deleted) {
          return;
        }

        state.newsFeed
          .filter(
            ({ type, data }) =>
              type === NewsFeedType.STORY_CARD_ITEM &&
              (data as StoryCardItemData).item.eventId === eventId
          )
          .forEach((feedItem) => {
            (feedItem.data as StoryCardItemData).item.event = null;
          });

        state.newsFeed
          .filter(({ type }) => type === NewsFeedType.HORIZONTAL_STORY_FEED)
          .forEach((feedItem) => {
            (feedItem.data as HorizontalStoryFeedData).items
              .filter((card) => card.eventId === eventId)
              .forEach((card) => {
                card.event = null;
              });
          });
      })
      .addMatcher(
        isAnyOf(updateCardEvent.fulfilled, eventUpdateChannels.fulfilled),
        (state, action) => {
          state.newsFeed
            .filter(
              ({ type, data }) =>
                type === NewsFeedType.STORY_CARD_ITEM &&
                (data as StoryCardItemData).item.eventId === action.payload.id
            )
            .forEach((feedItem) => {
              (feedItem.data as StoryCardItemData).item.event = action.payload;
            });

          state.newsFeed
            .filter(({ type }) => type === NewsFeedType.HORIZONTAL_STORY_FEED)
            .forEach((feedItem) => {
              (feedItem.data as HorizontalStoryFeedData).items
                .filter(({ eventId }) => eventId === action.payload.id)
                .forEach((card) => {
                  card.event = action.payload;
                });
            });
        }
      );
  },
});

export const { inAppMessageRemove } = newsFeedSlice.actions;

export default newsFeedSlice.reducer;
